import CategoryIcon from '@mui/icons-material/Category'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone'
import TimelineIcon from '@mui/icons-material/Timeline'
import {Box, Chip, IconButton, Tooltip, Typography} from '@mui/material'
import type {GridColDef, GridSortModel} from '@mui/x-data-grid'
import type {FC} from 'react'
import {useEffect, useMemo, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import EditLogbookEntryModal from './EditLogbookEntryModal'
import {useGetAllLogbookEntriesQuery} from '@apis/base'
import {useDeleteLogbookEntryMutation} from '@apis/logbook'
import {getFormattedDate} from '@app/util-functions'
import {useUserContext} from '@containers/App/UserProvider'
import type {UserContextProps} from '@containers/App/UserProvider'
import type {TenantLogbookEntriesResult} from '@interfaces/tenantLogbookEntries'
import BasicDataGrid from '@shared/components/base/BasicDataGrid/BasicDataGrid'
import BasicDeleteButton from '@shared/components/base/BasicDataGrid/BasicDeleteButton'
import BasicHierarchyFilterGroup from '@shared/components/base/filters/BasicHierarchyFilterGroup'
import Picker from '@shared/components/base/inputs/Picker'
import {useLogbookCategoryOptions} from '@shared/components/logbook/useLogbookCategoryOptions'
import {isLogbookEntryCreator} from '@shared/components/logbook/utils'
import SimpleSearchWithSearchParams from '@shared/components/SimpleSearchWithSearchParams'
import {
  DEFAULT_OFFSET,
  DEFAULT_PAGE_SIZE,
  STANDARD_PAGE_SIZE_OPTIONS,
} from '@shared/constants/preferences/pagination'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import {useMeasuredHeightOffset} from '@shared/hooks/useMeasuredHeightOffset'
import {useSeenOptionNames, withUnlistedSelection} from '@shared/hooks/useSeenOptionNames'
import {useFilterParams} from '@hooks/useFilterParams'

const COMMENTS_ORDERING_FIELD_MAP: Record<string, string> = {
  production_area: 'resolved_production_area_name',
  line: 'resolved_machine_name',
  equipment: 'machine_sub_system__name',
  category: 'category__name',
}

type LogbookEntry = TenantLogbookEntriesResult['results'][number]

/**
 * Where an entry lives in the app: its equipment's or line's dashboard, over
 * the days it covers, with the entry open. Area-level entries have no
 * dashboard of their own, so they link nowhere.
 */
export const logbookEntryLink = (entry: LogbookEntry): string | null => {
  const [type, targetId] = entry.machine_sub_system?.id
    ? ['equipment', entry.machine_sub_system.id]
    : entry.machine?.id
      ? ['line', entry.machine.id]
      : [null, null]
  if (!type || !targetId) {
    return null
  }
  const start = new Date(entry.t_start)
  start.setHours(0, 0, 0, 0)
  const end = new Date(entry.t_end)
  end.setHours(23, 59, 59, 999)
  const params = new URLSearchParams({
    time_range: 'null',
    start_date: start.toISOString(),
    end_date: end.toISOString(),
    entry_id: entry.id,
    entry_type: type,
    entry_target_id: targetId,
  })
  return `/${type}/${targetId}?${params.toString()}`
}

const LogbookTable: FC = () => {
  const [t] = useCustomTranslation('common')
  const [tLogbook] = useCustomTranslation('logbook')
  const navigate = useNavigate()
  const {currentUser} = useUserContext() as UserContextProps
  const [paginationModel, setPaginationModel] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_OFFSET,
  })
  const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'created_at', sort: 'desc'}])
  const [searchParams] = useSearchParams()
  // The category filter is the one this page owns; the hierarchy params above
  // still belong to BasicHierarchyFilterGroup.
  const {values: filterValues, setValue: setFilterValue} = useFilterParams({
    entryCategory: {},
  })
  // AB#5657 follow-up: search + hierarchy filters + category filter squished/overflowed
  // at narrower widths (no wrap anywhere in the chain). Own search + wrap below the
  // measured breakpoint, height driven by the row's own live-measured height.
  const [filterRowRef, gridHeightOffset] = useMeasuredHeightOffset(137)

  const productionAreaId = searchParams.get('productionAreaId') ?? ''
  const lineId = searchParams.get('lineId') ?? ''
  const equipmentId = searchParams.get('equipmentId') ?? ''
  const searchTerm = searchParams.get('searchLogbookEntries') ?? ''
  const entryCategory = filterValues.entryCategory

  useEffect(() => {
    setPaginationModel((prev) => ({...prev, page: 0}))
  }, [productionAreaId, lineId, equipmentId, searchTerm, entryCategory])

  const filters = useMemo(
    () => ({
      ...(productionAreaId ? {production_area: productionAreaId} : {}),
      ...(lineId ? {machine: lineId} : {}),
      ...(equipmentId ? {machine_sub_system: equipmentId} : {}),
      ...(searchTerm ? {search: searchTerm} : {}),
      ...(entryCategory ? {category: entryCategory} : {}),
    }),
    [productionAreaId, lineId, equipmentId, searchTerm, entryCategory],
  )

  const [editing, setEditing] = useState<LogbookEntry | null>(null)
  const [deleteEntry] = useDeleteLogbookEntryMutation()
  const {
    data: entries,
    isFetching,
    error,
    refetch,
  } = useGetAllLogbookEntriesQuery({
    filters,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    ordering:
      sortModel[0]?.field && sortModel[0]?.sort
        ? `${sortModel[0].sort === 'desc' ? '-' : ''}${
            COMMENTS_ORDERING_FIELD_MAP[sortModel[0].field] || sortModel[0].field
          }`
        : undefined,
  })

  // Every category of the tenant, searched on the server — not just the ones
  // on the rows of the current page.
  const categories = useLogbookCategoryOptions()
  const categoryNames = useSeenOptionNames(
    (entries?.results ?? []).flatMap((c) =>
      c.category ? [{id: c.category.id, name: c.category.name}] : [],
    ),
  )

  const formatDate = (value: string): string =>
    getFormattedDate(value, currentUser, {showDate: true, showYear: true, showTime: true})

  const rows = entries?.results ?? []

  const textColumn: GridColDef<LogbookEntry> = {
    field: 'text',
    headerName: t('logbookTable.text'),
    editable: false,
    flex: 2,
    sortable: false,
    renderCell: ({row}) => (
      // Full cell height so it centres like the plain-text columns, and the
      // text inherits the grid's own font rather than body2.
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, height: '100%', minWidth: 0}}>
        {row.corrects && (
          <Chip size="small" color="info" label={tLogbook('correction')} sx={{flexShrink: 0}} />
        )}
        {row.corrections?.length > 0 && (
          <Chip
            size="small"
            color="warning"
            label={tLogbook('corrected_later')}
            sx={{flexShrink: 0}}
          />
        )}
        <Typography variant="inherit" noWrap title={row.text}>
          {row.text}
        </Typography>
      </Box>
    ),
  }

  // Action columns: fixed width, centred, no sorting or menu.
  const actionColumn = (
    field: string,
    headerName: string,
    renderCell: GridColDef<LogbookEntry>['renderCell'],
  ): GridColDef<LogbookEntry> => ({
    field,
    headerName,
    width: field === 'timeline' ? 170 : 80,
    sortable: false,
    disableColumnMenu: true,
    align: 'center',
    headerAlign: 'center',
    renderCell,
  })

  const columns: GridColDef<LogbookEntry>[] = [
    textColumn,
    {
      field: 'category',
      headerName: t('logbookTable.category'),
      editable: false,
      flex: 1,
      valueGetter: (_value, row) => row.category?.name ?? '',
    },
    {
      field: 'author',
      headerName: tLogbook('author'),
      editable: false,
      flex: 1,
      sortable: false,
      valueGetter: (_value, row) =>
        [row.user?.first_name, row.user?.last_name].filter(Boolean).join(' ') ||
        row.user?.email ||
        '',
    },
    {
      field: 'production_area',
      headerName: t('logbookTable.production_area'),
      editable: false,
      flex: 1,
      valueGetter: (_value, row) => row.production_area?.name ?? '',
    },
    {
      field: 'line',
      headerName: t('logbookTable.line'),
      editable: false,
      flex: 1,
      valueGetter: (_value, row) => row.machine?.name ?? '',
    },
    {
      field: 'equipment',
      headerName: t('logbookTable.equipment'),
      editable: false,
      flex: 1,
      valueGetter: (_value, row) => row.machine_sub_system?.name ?? '',
    },
    {
      field: 't_start',
      headerName: tLogbook('time_window'),
      editable: false,
      flex: 1.4,
      renderCell: ({row}) => `${formatDate(row.t_start)} – ${formatDate(row.t_end)}`,
    },
    {
      field: 'created_at',
      headerName: t('logbookTable.created_at'),
      editable: false,
      flex: 1,
      renderCell: ({value}) => (value ? formatDate(value as string) : ''),
    },
    actionColumn('timeline', tLogbook('table.production_timeline'), ({row}) => {
      const link = logbookEntryLink(row)
      // Area-wide entries have no dashboard of their own.
      return link ? (
        <Tooltip title={tLogbook('table.production_timeline')}>
          <IconButton
            size="small"
            onClick={() => navigate(link)}
            aria-label={tLogbook('table.production_timeline')}
            data-cy="logbook-table-timeline"
          >
            <TimelineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null
    }),
    actionColumn('edit', tLogbook('edit'), ({row}) => {
      const isAuthor = isLogbookEntryCreator(currentUser, row.user)
      return (
        <Tooltip title={isAuthor ? tLogbook('edit') : tLogbook('errors.cannot_update_entry')}>
          <span>
            <IconButton
              size="small"
              disabled={!isAuthor}
              onClick={() => setEditing(row)}
              aria-label={tLogbook('edit')}
              data-cy="logbook-table-edit-button"
            >
              <EditTwoToneIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )
    }),
    actionColumn('delete', tLogbook('delete'), ({row}) =>
      isLogbookEntryCreator(currentUser, row.user) ? (
        <BasicDeleteButton
          entityId={row.id}
          entityName={row.text.length > 40 ? `${row.text.slice(0, 40)}…` : row.text}
          deleteMutation={(id) => deleteEntry({entryId: id}).unwrap()}
          refetch={refetch}
          deleteMessageKey={tLogbook('delete_entry_confirm')}
        />
      ) : (
        <Tooltip title={tLogbook('errors.cannot_delete_entry')}>
          <span>
            <IconButton size="small" disabled aria-label={tLogbook('delete')}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    ),
  ]

  const categoryFilter = (
    <Picker
      {...categories}
      options={withUnlistedSelection(
        categories.options,
        entryCategory ? [entryCategory] : [],
        categoryNames,
      )}
      label={t('logbookTable.category')}
      icon={<CategoryIcon />}
      value={entryCategory}
      onChange={(val) => setFilterValue('entryCategory', val)}
      dataCy="entries-category"
    />
  )

  return (
    <Box padding={3} data-testid="entries-table-box">
      <BasicDataGrid
        columns={columns}
        rows={rows}
        rowCount={entries?.count || 0}
        title="logbook"
        loading={isFetching}
        error={false}
        queryError={error}
        rowHeight={50}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={(model) => {
          setSortModel(model)
          setPaginationModel((prev) => ({...prev, page: 0}))
        }}
        sortModel={sortModel}
        pageSizeOptions={STANDARD_PAGE_SIZE_OPTIONS}
        height={gridHeightOffset}
        noRowsMessage={t('no_data')}
        titleSearchParams="searchLogbookEntries"
        // Own search rendered below, inside the wrapped filter row, so it wraps together
        // with the hierarchy + category filters instead of squishing against them in
        // BasicDataGrid's own nowrap header row.
        hideSearch
        disableRowSelectionOnClick
        customLeftHeaderActions={
          <Box
            ref={filterRowRef}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 0.5,
              rowGap: 1,
              alignItems: 'center',
            }}
            data-testid="mbai-filter-entries"
          >
            <Box sx={{width: 200, flexShrink: 0}}>
              <SimpleSearchWithSearchParams title="searchLogbookEntries" size="small" />
            </Box>
            <BasicHierarchyFilterGroup />
            <Box sx={{width: 200, flexShrink: 0}} data-testid="mbai-filter-entries-search">
              {categoryFilter}
            </Box>
          </Box>
        }
      />
      <EditLogbookEntryModal entry={editing} onClose={() => setEditing(null)} onSaved={refetch} />
    </Box>
  )
}

export default LogbookTable
